import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal, computed, OnDestroy, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, Params } from '@angular/router';
import { Observable, tap, catchError, of, finalize, map, switchMap, Subject, shareReplay } from 'rxjs';
import { AuthApiService } from '../data-access/auth-api.service';
import { APP_CONFIGS, AppNavigationService } from '@dental-ui/shared/app-navigation';
import {
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
  AUTH_SESSION_END_REASON_KEY,
  AUTH_SESSION_END_FORCE_LOGOUT,
  AUTH_SESSION_END_USER_LOGOUT,
  AUTH_SESSION_END_EXPIRED,
  AUTH_VISIBILITY_FORCE_REFRESH_MS,
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  REFRESH_TOKEN_EXPIRES_COOKIE,
  TOKEN_EXPIRES_COOKIE,
  EMAIL_COOKIE,
  LANGUAGE_COOKIE,
  ACCOUNT_APP_URL_COOKIE,
  isAccountApp,
  getAppTypeByName,
  mapPackageNameToAppName,
  canAccessClinicByRole,
  ACCOUNT_APP_NAME,
  ACCOUNT_ENTRY_PATH,
  CAD_APP_NAME,
  CLINIC_APP_NAME,
  LAB_APP_NAME,
  APP_ENTRY_PATH_MAIN,
  LAB_ENTRY_PATH_DEV,
  LAB_ENTRY_PATH_RELEASE,
  AUTH_BROADCAST_CHANNEL,
  AUTH_LOGOUT_BEACON_COOKIE,
  AUTH_LOGOUT_BEACON_POLL_MS,
  AUTH_LOGOUT_BEACON_TTL_DAYS,
  AUTH_LOGOUT_BEACON_VALUE_SEPARATOR,
  LOGOUT_EVENT,
  LOGIN_EVENT,
  AUTH_PARAM_KEYS,
  AUTH_REDIRECT_MODE_MODULE,
  AuthRedirectMode,
  SUBSCRIPTION_DTO_KEY_DATA,
  SUBSCRIPTION_DTO_KEY_PACKAGE_NAME,
  SUBSCRIPTION_DTO_KEY_PACKAGE_NAME_PASCAL,
  SUBSCRIPTION_DTO_KEY_ID,
  SUBSCRIPTION_DTO_KEY_ID_PASCAL,
} from '@dental-ui/shared/consts';
import { TranslateService } from '@dental-ui/shared/i18n';
import { isDevelopmentBuild } from '../guards/development.guard';

import { LoginDto } from '../models/lib/login-request.model';
import { LoginResponseDto } from '../models/lib/login-response.model';
import { CheckActiveSessionDto } from '../models/lib/check-active-session.model';
import { ResponseModel } from '../models/lib/response.model';
import { RefreshTokenModelDto } from '../models/lib/refresh-token-model.model';
import { UserSettingsDto } from '../models/lib/user-settings.model';
import { UserProfileSessionUpdate } from '../models/lib/user-profile-session-update.model';
import { SubscriptionGetAllByFilterRequest, SubscriptionFilterRequestDto, SubscriptionDto } from '../models/lib/subscription.model';
import { AuthLogoutBeacon } from '../models/lib/auth-logout-beacon.model';

export type loginUser = LoginResponseDto;
export type { AuthRedirectMode };

@Injectable({
  providedIn: 'root',
})
export class AuthStateService implements OnDestroy {
  private readonly accountApiService: AuthApiService = inject(AuthApiService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT, { optional: true });
  private readonly appNavigation: AppNavigationService | null = inject(AppNavigationService, { optional: true });
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly ngZone = inject(NgZone);

  // BroadcastChannel for cross-tab communication
  private readonly broadcastChannel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_BROADCAST_CHANNEL) : null;

  // Track last broadcast timestamp to avoid processing our own messages
  private lastBroadcastTimestamp = 0;
  /** Bu sekmedeki oturumun başlangıcı — logout beacon'ı eski saymamak için */
  private sessionStartedAtMs = 0;
  /** Bu oturumda paylaşılan auth cookie'leri kullanıldı mı (cross-origin logout tespiti) */
  private sessionHadSharedCookies = false;
  private logoutBeaconTimer: ReturnType<typeof setInterval> | null = null;
  private remoteLogoutInProgress = false;

  // Refresh token in progress - eşzamanlı isteklerde tek refresh çağrısı için
  private refreshInProgress: Observable<string | null> | null = null;
  /**
   * Oturum kimliği — URL handoff / login eski in-flight refresh'in yeni oturumu
   * silmesini engeller (localhost account→lab: eski lab localStorage refresh yarışı).
   */
  private sessionEpoch = 0;

  // Proaktif sessiz token yenileme zamanlayıcısı
  private silentRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Bu sayfa yüklemesinde Auth/Me (veya login) ile oturumun bu origin'e
   * (cookie + localStorage) yazıldığını işaretler. CAD gibi uzun oturumlarda
   * access dolunca refresh için email/access/refresh şart.
   */
  private sessionWrittenViaMe = false;

  /** Sekme tekrar görünür olunca token kontrolü (setTimeout throttle'a karşı) */
  private readonly onVisibilityChange = (): void => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return;
    }
    this.applyRemoteLogoutIfNeeded();
    if (!this._isAuthenticated() || this.isRefreshTokenExpired()) {
      return;
    }
    console.info('[Auth][Görünürlük] Sekme aktif — token kontrol ediliyor');
    if (this.isTokenExpired()) {
      this.ensureValidToken().subscribe({
        next: (token) =>
          console.info(
            '[Auth][Görünürlük]',
            token ? 'Token yenileme başarılı' : 'Geçici hata (oturum açık kaldı)'
          ),
        error: (err) =>
          console.warn('[Auth][Görünürlük] Token kontrolü hata verdi', err),
      });
    } else {
      // Access henüz "expired" değil ama arka planda uzun süre kalındıysa proaktif yenile
      const expirationStr =
        this.getCookie(TOKEN_EXPIRES_COOKIE) ?? this._user()?.expiration ?? null;
      if (!expirationStr) {
        return;
      }
      const msLeft = new Date(expirationStr).getTime() - Date.now();
      if (msLeft > 0 && msLeft < AUTH_VISIBILITY_FORCE_REFRESH_MS) {
        console.info('[Auth][Görünürlük] Erişim tokenı süresi <2 dk — zorla yenileme başlıyor');
        this.forceRefreshToken().subscribe({
          next: (token) =>
            console.info(
              '[Auth][Görünürlük]',
              token ? 'Zorla yenileme başarılı' : 'Zorla yenileme geçici başarısız'
            ),
          error: (err) =>
            console.warn('[Auth][Görünürlük] Zorla yenileme hatası', err),
        });
      }
    }
  };

  /** window focus — görünürlük tetiklenmese de cross-origin logout beacon kontrolü */
  private readonly onWindowFocus = (): void => {
    this.applyRemoteLogoutIfNeeded();
  };

  /**
   * Aynı origin diğer sekme localStorage'dan token sildiğinde (BroadcastChannel kaçarsa)
   */
  private readonly onStorageEvent = (event: StorageEvent): void => {
    if (event.key !== AUTH_TOKEN_KEY || event.newValue !== null) {
      return;
    }
    if (!this._isAuthenticated()) {
      return;
    }
    this.ngZone.run(() => {
      this.handleRemoteSessionEnd(
        AUTH_SESSION_END_USER_LOGOUT,
        'aynı origin storage olayı'
      );
    });
  };

  // Subscribed app names cache
  private _subscribedApps: string[] | null = null;

  // Language change subscription - tüm kütüphaneler bu Observable'a subscribe olabilir
  public readonly languageChange$: Observable<string>;
  private readonly languageChangeSubject = new Subject<string>();

  /**
   * ForceLogout UI (dialog) tetikleyicisi — SignalR veya diğer sekmeden gelen broadcast.
   * Auth katmanı UI dialog açmaz; ForceLogoutDialogService dinler.
   */
  private readonly forceLogoutRequestSubject = new Subject<void>();
  public readonly forceLogoutRequested$ = this.forceLogoutRequestSubject.asObservable();
  /**
   * ForceLogout dialog açık / bekleniyor — 401/refresh yarışı oturumu diyalogsuz kapatmasın.
   */
  private forceLogoutUiPending = false;
  /**
   * Uygulamalar arası URL handoff / setAuthState sonrası kısa süre SignalR ForceLogout
   * yoksayılır (account→lab geçişinde eski hub bağlantısı grubu tekmeleyince yanlış dialog).
   */
  private forceLogoutSuppressUntilMs = 0;
  private static readonly FORCE_LOGOUT_HANDOFF_GRACE_MS = 8_000;

  // Signals
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _user = signal<loginUser | null>(null);
  private readonly _token = signal<string | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _redirectOverlay = signal<AuthRedirectMode | null>(null);
  private readonly _redirectOverlayApp = signal<string | null>(null);
  private readonly _redirectOverlaySourceApp = signal<string | null>(null);
  private redirectOverlayTimeout: ReturnType<typeof setTimeout> | undefined;

  // Public computed signals
  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly user = computed(() => this._user());
  readonly token = computed(() => this._token());
  readonly isLoading = computed(() => this._isLoading());
  readonly error = computed(() => this._error());
  readonly redirectOverlay = computed(() => this._redirectOverlay());
  readonly redirectOverlayApp = computed(() => this._redirectOverlayApp());
  readonly redirectOverlaySourceApp = computed(() => this._redirectOverlaySourceApp());

  /**
   * Autobar seat assigned (`isDesignBar === true` from login / Auth/Me).
   * Required to open CAD from design-project list.
   */
  readonly hasDesignBarAccess = computed(() => this._user()?.isDesignBar === true);

  /**
   * CAD mesh export / share allowed for the active CAD/Lab package
   * (`isExportable` / `isShareable` from GetAllSubscriptionByFilter).
   * `null` until subscriptions are loaded.
   */
  private readonly _cadExportable = signal<boolean | null>(null);
  private readonly _cadShareable = signal<boolean | null>(null);
  private readonly _activeSubscriptionsSignal = signal<SubscriptionDto[] | null>(null);
  private _activeSubscriptions: SubscriptionDto[] | null = null;
  /** In-flight GetAllSubscriptionByFilter — concurrent callers share one request. */
  private subscriptionsLoadInFlight: Observable<SubscriptionDto[]> | null = null;

  /** Cached active subscriptions (`null` until first load after login/Me). */
  readonly activeSubscriptions = computed(() => this._activeSubscriptionsSignal());
  /** True when package allows CAD export. */
  readonly isCadExportable = computed(() => this._cadExportable() === true);
  /** True when package explicitly disallows CAD export (button should be disabled). */
  readonly isCadExportBlocked = computed(() => this._cadExportable() === false);
  /** True when package allows CAD share. */
  readonly isCadShareable = computed(() => this._cadShareable() === true);
  /** True when package explicitly disallows CAD share (button should be disabled). */
  readonly isCadShareBlocked = computed(() => this._cadShareable() === false);

  constructor() {
    // Setup language change Observable - tüm kütüphaneler bu Observable'a subscribe olabilir
    this.languageChange$ = this.languageChangeSubject.asObservable();

    // TranslateService'den dil değişikliklerini dinle ve Subject'e yay
    this.translateService.languageChange.subscribe(language => {
      this.languageChangeSubject.next(language);
      // Cookie'yi güncelle
      this.setLanguageCookie(language);
    });

    // Setup cross-tab communication listener
    this.setupBroadcastListener();
    this.setupStorageListener();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.onWindowFocus);
    }

    // URL handoff (account→lab token query) varken eski localStorage oturumunu yükleme —
    // aksi halde eski refresh 401'i yeni oturumu login?logout=true ile düşürür.
    if (this.hasAuthParamsInUrl()) {
      console.info(
        '[Auth] URL oturum parametreleri var — storage bootstrap atlandı (handoff)'
      );
    } else {
      // Oturumu storage'dan hemen oku (senkron). Guard çalışmadan önce auth dolu olsun ki
      // diğer sekmede açılan oturum bu sekmede de görünsün. HTTP kullanmıyor, sadece localStorage/sessionStorage.
      this.loadStoredAuth();
    }

    // HTTP client'ın hazır olması için initialization'ı erteliyoruz (Firefox için kritik - yoksa sayfa yüklenmiyor)
    // 
    // NEDEN ASENKRON ÇAĞRI GEREKLİ?
    // Constructor içinde senkron olarak HTTP çağrısı yaparsak, HTTP client henüz tam hazır olmayabilir.
    // Angular constructor sırasında servisleri inject eder ama internal setup henüz tamamlanmamış olabilir.
    // Firefox bu duruma özellikle hassas ve sayfa yüklenmeyebilir.
    //
    // ÇÖZÜM: queueMicrotask() kullanarak callback'i asenkron hale getiriyoruz
    // - Callback constructor BİTTİKTEN SONRA çalışacak (HTTP client hazır olacak)
    // - queueMicrotask() setTimeout(0)'a göre daha hızlı (microtask queue, macrotask queue'dan önce çalışır)
    // - Her iki yöntem de çalışır ama queueMicrotask() daha performanslı
    queueMicrotask(() => {
      // Cookie'den dili yükle ve TranslateService'e uygula
      const languageFromCookie = this.getCookie(LANGUAGE_COOKIE);
      if (languageFromCookie) {
        this.translateService.setLanguage(languageFromCookie).catch(err => {
          console.warn('[Auth] Çerezden dil ayarlanamadı:', err);
        });
      }

      // Guard persistAuthParamsFromUrl handoff'u işleyecek — eski storage/cookie Me yarışını başlatma
      if (this.hasAuthParamsInUrl()) {
        return;
      }

      // In development (localhost), prioritize localStorage since cookies don't work across ports
      // In production, try cookies first, then fall back to localStorage
      const isLocalhost = typeof location !== 'undefined' &&
        (location.hostname === 'localhost' || location.hostname === '127.0.0.1');

      if (isLocalhost) {
        // Development: Load from localStorage first
        this.loadStoredAuth();
        // Also try cookies in case they exist (same port)
        if (!this._isAuthenticated()) {
          this.bootstrapSessionFromCookieOrStorage();
        }
      } else {
        // Production: Try cookies first, then localStorage
        // Guard (persistAuthParamsFromUrl) zaten auth'u set ettiyse tekrar refresh yapma
        if (!this._isAuthenticated()) {
          this.bootstrapSessionFromCookieOrStorage();
          if (!this._isAuthenticated()) {
            this.loadStoredAuth();
          }
        }
      }
    });
  }

  /** Account→Lab vb. URL'de accessToken+refreshToken var mı (hash veya search). */
  private hasAuthParamsInUrl(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const searchParams = new URLSearchParams(window.location.search);
    const hashParts = window.location.hash.split('?');
    const hashParams =
      hashParts.length > 1 ? new URLSearchParams(hashParts[1]) : new URLSearchParams();
    const access =
      searchParams.get('accessToken') || hashParams.get('accessToken');
    const refresh =
      searchParams.get('refreshToken') || hashParams.get('refreshToken');
    return !!(access && refresh);
  }

  /** Yeni oturum kaynağı (login / URL handoff) — eski in-flight refresh sonuçlarını geçersiz kılar. */
  private bumpSessionEpoch(reason: string): void {
    this.sessionEpoch += 1;
    this.refreshInProgress = null;
    console.info(`[Auth] Oturum epoch=${this.sessionEpoch} (${reason})`);
  }

  /**
   * Login öncesi aktif oturum kontrolü yapar.
   */
  checkActiveSession(
    loginRequest: LoginDto
  ): Observable<ResponseModel<CheckActiveSessionDto>> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.accountApiService.checkActiveSession(loginRequest, false).pipe(
      tap((response: ResponseModel<CheckActiveSessionDto>) => {
        if (!response.success) {
          this._error.set(response.message || 'Session check failed');
        }
      }),
      catchError((error: unknown) => {
        const errorMessage = this.readApiErrorMessage(
          error,
          'An error occurred during session check',
        );
        this._error.set(errorMessage);
        return of({
          success: false,
          message: errorMessage,
        } as ResponseModel<CheckActiveSessionDto>);
      }),
      finalize(() => {
        this._isLoading.set(false);
      })
    );
  }

  login(
    loginRequest: LoginDto,
    rememberMe = false
  ): Observable<ResponseModel<LoginResponseDto>> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.accountApiService.login(loginRequest, false).pipe(
      tap((response: ResponseModel<LoginResponseDto>) => {
        if (response.success && response.data) {
          this.setAuthState(response.data, rememberMe);
          // Fresh session — always refresh package cache after login.
          this.prefetchActiveSubscriptions(true);
        } else {
          this.handleFailedLogin(response.message);
        }
      }),
      catchError((error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An error occurred during login';
        this.handleFailedLogin(errorMessage);
        return of({
          success: false,
          message: errorMessage,
        } as ResponseModel<LoginResponseDto>);
      }),
      finalize(() => {
        this._isLoading.set(false);
      })
    );
  }

  logout(): void {
    this.logSessionEnd(AUTH_SESSION_END_USER_LOGOUT, 'Kullanıcı çıkış yaptı');
    // Diğer sekmeler reason ile ayırt edebilsin (login?logout=true clearAuth broadcast etmez)
    this.broadcastLogout(AUTH_SESSION_END_USER_LOGOUT);
    this.showRedirectOverlay('logout');
    this._isLoading.set(true);

    this.accountApiService
      .logout()
      .pipe(finalize(() => {
        this._isLoading.set(false);
      }))
      .subscribe({
        next: () => this.handlePostLogout(),
        error: () => this.handlePostLogout(),
      });
  }

  /**
   * SignalR MessageHub "ForceLogout" — kullanıcı dialogda Tamam'a bastıktan sonra
   * bu oturumu sonlandırır. Logout API çağırmaz (sunucu zaten invalidate etmiş olabilir).
   * @param broadcast Diğer sekmelere ForceLogout reason'ı gönderilsin mi
   */
  forceRemoteLogout(broadcast = true): void {
    this.forceLogoutUiPending = false;
    if (!this._isAuthenticated()) {
      // Dialog sonrası / yarışta bellek temizlenmiş olabilir — yine de login'e git
      this.navigateToAccountLogin();
      return;
    }
    this.logSessionEnd(
      AUTH_SESSION_END_FORCE_LOGOUT,
      'Hesabınız başka bir cihazda açıldı'
    );
    this.endSessionAndRedirect(AUTH_SESSION_END_FORCE_LOGOUT, broadcast);
  }

  /** ForceLogout dialog bekleniyor mu (interceptor / refresh logout'u bastırmak için) */
  isForceLogoutUiPending(): boolean {
    return this.forceLogoutUiPending;
  }

  /**
   * UI katmanının ForceLogout dialog'unu açması için (SignalR / broadcast / revoke).
   * Sessiz yenilemeyi durdurur; 401 yarışının diyalogsuz redirect yapmasını engeller.
   */
  requestForceLogoutDialog(): void {
    if (Date.now() < this.forceLogoutSuppressUntilMs) {
      console.warn(
        '[Auth] ForceLogout dialog bastırıldı — uygulamalar arası oturum handoff grace'
      );
      return;
    }

    this.forceLogoutUiPending = true;
    this.clearSilentRefreshTimer();

    if (!this._isAuthenticated()) {
      return;
    }
    this.forceLogoutRequestSubject.next();
  }

  /** Account→Lab vb. geçiş / login sonrası kısa ForceLogout grace başlatır. */
  private beginForceLogoutHandoffGrace(): void {
    this.forceLogoutSuppressUntilMs =
      Date.now() + AuthStateService.FORCE_LOGOUT_HANDOFF_GRACE_MS;
  }

  forgetPassword(email: string): Observable<ResponseModel<void>> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.accountApiService.forgetPassword(email, false).pipe(
      tap((response: ResponseModel<void>) => {
        if (!response.success) {
          this._error.set(response.message || 'Password reset failed');
        }
      }),
      catchError((error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An error occurred during password reset';
        this._error.set(errorMessage);
        return of({
          success: false,
          message: errorMessage,
        } as ResponseModel<void>);
      }),
      finalize(() => {
        this._isLoading.set(false);
      })
    );
  }

  /**
   * @param broadcast Diğer sekmelere bildirilsin mi
   * @param reason Broadcast reason — yanlışlıkla "UserLogout" görünmesini önlemek için zorunlu tutulur
   */
  clearAuth(
    broadcast = true,
    reason: string = AUTH_SESSION_END_EXPIRED
  ): void {
    this.clearSilentRefreshTimer();
    this.stopLogoutBeaconWatch();
    this.forceLogoutUiPending = false;
    this._token.set(null);
    this._user.set(null);
    this._isAuthenticated.set(false);
    this._isLoading.set(false);
    this._error.set(null);
    this.refreshInProgress = null;
    this._subscribedApps = null;
    this._activeSubscriptions = null;
    this._activeSubscriptionsSignal.set(null);
    this.subscriptionsLoadInFlight = null;
    this._cadExportable.set(null);
    this._cadShareable.set(null);
    this.sessionWrittenViaMe = false;
    this.sessionHadSharedCookies = false;

    this.clearStoredAuth();

    if (broadcast) {
      this.broadcastLogout(reason);
    }
  }

  private loadStoredAuth(): void {
    // Başka origin'de logout olduysa localStorage hayalet oturumunu geri yükleme
    if (this.parseLogoutBeacon()) {
      console.info('[Auth] Logout beacon var — storage oturumu yüklenmedi');
      this.clearStoredAuth();
      return;
    }

    const token = this.getStoredValue(AUTH_TOKEN_KEY);
    const user = this.loadStoredUser();

    if (!token || !user) {
      return;
    }

    // loadStoredUser already returns a LoginResponseDto instance
    this._token.set(token);
    this._user.set(user);

    // Refresh token da dolmuşsa oturumu temizle
    if (this.isRefreshTokenExpired()) {
      this.logSessionEnd(AUTH_SESSION_END_EXPIRED, 'önyüklemede yenileme tokenı süresi dolmuş');
      this._token.set(null);
      this._user.set(null);
      this._isAuthenticated.set(false);
      this.clearStoredAuth();
      return;
    }

    // Kullanıcı bilgisi ve geçerli/yenilenebilir refresh token var, oturumu aktif işaretle
    this._isAuthenticated.set(true);
    this.markLocalSessionActive(false);

    // Access token süresi dolmuşsa, ensureValidToken() ile tek nokta üzerinden yenilemeyi başlat
    if (this.isTokenExpired()) {
      queueMicrotask(() => {
        this.ensureValidToken().subscribe();
      });
    } else {
      this.scheduleSilentRefresh(user.expiration);
    }
  }

  clearError(): void {
    this._error.set(null);
  }


  /**
   * Auth durumunu kalıcı hale getirir (clinic gibi diğer uygulamalarda kullanım için public)
   */
  setAuthState(
    data: LoginResponseDto,
    rememberMe: boolean
  ): void {
    // HTTP JSON PascalCase gelebilir — LoginResponseDto üzerinden normalize et
    const user = new LoginResponseDto();
    user.setData(data);

    const { accessToken, refreshToken } = user;
    this.forceLogoutUiPending = false;
    this.bumpSessionEpoch('setAuthState');
    this._token.set(accessToken);
    this._user.set(user);
    this._isAuthenticated.set(true);

    this.setSessionValue(AUTH_TOKEN_KEY, accessToken, rememberMe);
    this.setSessionValue(USER_KEY, JSON.stringify(user), rememberMe);

    // API yeni refreshToken döndürmediyse mevcut değeri silme (oturum kırılır)
    if (refreshToken) {
      this.setSessionValue(REFRESH_TOKEN_KEY, refreshToken, rememberMe);
    }

    this.setAuthCookies(user);
    this.sessionWrittenViaMe = true;
    this.markLocalSessionActive(true);
    this.beginForceLogoutHandoffGrace();

    // Proaktif sessiz token yenilemeyi planla
    this.scheduleSilentRefresh(user.expiration);

    // Broadcast login to other tabs/windows
    this.broadcastLogin();
  }

  /**
   * Sessiz yenileme için email + access + refresh bu origin'de hazır mı?
   */
  canSilentRefresh(): boolean {
    const email =
      this.getCookie(EMAIL_COOKIE) ??
      this._user()?.email ??
      '';
    const accessToken =
      this._token() ??
      this.getCookie(ACCESS_TOKEN_COOKIE) ??
      this.getStoredValue(AUTH_TOKEN_KEY);
    const refreshToken =
      this.getCookie(REFRESH_TOKEN_COOKIE) ??
      this.getStoredValue(REFRESH_TOKEN_KEY);
    return !!(email && accessToken && refreshToken);
  }

  hasSessionBeenWrittenViaMe(): boolean {
    return this.sessionWrittenViaMe;
  }

  /** MessageHub ForceLogout grubuna katılmak için communicationId dolu mu */
  hasCommunicationId(): boolean {
    return !!this._user()?.communicationId?.trim();
  }

  /**
   * Auth/Me ile oturumu bu origin'e yazar (CAD port localStorage'ı Lab'den ayrıdır).
   * Access süresi dolmadan önce bir kez çağrılmalı ki sessiz refresh bilgileri bulabilsin.
   * communicationId yoksa (URL handoff / eksik storage) her zaman /Me çağrılır — ForceLogout için gerekli.
   */
  hydrateSessionViaMe(): Observable<boolean> {
    if (
      this.sessionWrittenViaMe &&
      this.canSilentRefresh() &&
      this.hasCommunicationId()
    ) {
      return of(true);
    }

    return this.forceRefreshSessionViaMe();
  }

  /**
   * Always calls Auth/Me (no cache skip) so fields like `isDesignBar` stay current.
   * Used by CAD designBarGuard — seat may be revoked while the session is still open.
   */
  forceRefreshSessionViaMe(): Observable<boolean> {
    const accessToken =
      this._token() ??
      this.getCookie(ACCESS_TOKEN_COOKIE) ??
      this.getStoredValue(AUTH_TOKEN_KEY);

    if (!accessToken) {
      console.warn('[Auth] forceRefreshSessionViaMe: access token yok');
      return of(false);
    }

    console.info('[Auth] Auth/Me ile oturum zorla yenileniyor (isDesignBar dahil)');
    return this.refreshSession(accessToken).pipe(
      switchMap((ok) => {
        if (ok) {
          return of(true);
        }
        // /Me soft-fail: refresh ile kurtarmayı dene, sonra tekrar /Me
        if (!this.isRefreshTokenExpired()) {
          return this.ensureValidToken().pipe(
            switchMap((token) => {
              if (!token) {
                return of(false);
              }
              return this.refreshSession(token);
            })
          );
        }
        return of(false);
      })
    );
  }

  /**
   * Mevcut kullanıcının şube bilgisini oturumda ve depolama alanlarında günceller.
   * (Kullanıcı kendi şubesini değiştirdiğinde oturumu yenilemeden güncelleme yapmak için kullanılır)
   */
  updateUserBranchInSession(branchId: string, branchDescription?: string): void {
    const currentUser = this._user();
    if (!currentUser) return;

    // Signal state'i güncelle
    const updatedUser = new LoginResponseDto();
    updatedUser.setData(currentUser);
    updatedUser.branchId = branchId;
    if (branchDescription) {
      updatedUser.branchDescription = branchDescription;
    }
    this._user.set(updatedUser);

    // Depolama alanlarını güncelle (localStorage/sessionStorage)
    const rememberMe = this.resolveRememberMePreference();
    this.setSessionValue(
      USER_KEY,
      JSON.stringify(updatedUser),
      rememberMe
    );

    // Auth cookie'lerini güncelle (Bazı akışlar auth bilgilerini cookie'den okur)
    this.setAuthCookies(updatedUser);

    // Diğer sekmelere bildir
    this.broadcastLogin();
  }

  /**
   * Mevcut kullanıcının userSettings bilgisini oturumda ve depolama alanlarında günceller.
   * (Settings kaydı sonrası yeniden giriş gerektirmeden mouseControl vb. değerlerin CAD'e yansıması için)
   */
  updateUserSettingsInSession(userSettings: UserSettingsDto): void {
    const currentUser = this._user();
    if (!currentUser || !userSettings) return;

    const updatedUser = new LoginResponseDto();
    updatedUser.setData(currentUser);

    const settings = new UserSettingsDto();
    settings.setData(userSettings);
    updatedUser.userSettingsDto = settings;

    this._user.set(updatedUser);

    const rememberMe = this.resolveRememberMePreference();
    this.setSessionValue(USER_KEY, JSON.stringify(updatedUser), rememberMe);
    this.setAuthCookies(updatedUser);
    this.broadcastLogin();
  }

  /**
   * Mevcut kullanıcının ad/soyad bilgisini oturumda ve depolama alanlarında günceller.
   * (Profil kaydı sonrası header vb. yerlerin yeniden giriş gerektirmeden güncellenmesi için)
   */
  updateUserProfileInSession(profile: UserProfileSessionUpdate): void {
    const currentUser = this._user();
    if (!currentUser || !profile) return;

    const updatedUser = new LoginResponseDto();
    updatedUser.setData(currentUser);

    if (profile.firstName !== undefined) {
      updatedUser.firstName = profile.firstName;
    }
    if (profile.lastName !== undefined) {
      updatedUser.lastName = profile.lastName;
    }

    this._user.set(updatedUser);

    const rememberMe = this.resolveRememberMePreference();
    this.setSessionValue(USER_KEY, JSON.stringify(updatedUser), rememberMe);
    this.setAuthCookies(updatedUser);
    this.broadcastLogin();
  }

  private handleFailedLogin(message?: string): void {
    this._error.set(message || 'Login failed');
    this._isAuthenticated.set(false);
    this._token.set(null);
    this._user.set(null);
  }

  private readApiErrorMessage(error: unknown, fallback: string): string {
    const body = error instanceof HttpErrorResponse ? error.error : null;
    let payload: unknown = body;
    if (typeof body === 'string') {
      try {
        payload = JSON.parse(body);
      } catch {
        const text = body.trim();
        return text || fallback;
      }
    }
    if (payload && typeof payload === 'object' && 'message' in payload) {
      const message = (payload as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
    }
    return fallback;
  }

  private handlePostLogout(): void {

    this.navigateToAccountLogin();
  }

  private setSessionValue(
    key: string,
    value: string,
    rememberMe: boolean
  ): void {
    const primaryStorage = rememberMe ? localStorage : sessionStorage;
    const secondaryStorage = rememberMe ? sessionStorage : localStorage;

    primaryStorage.setItem(key, value);
    secondaryStorage.removeItem(key);

    // rememberMe=false iken de yeni sekmeler oturumu görsün diye localStorage'a yaz
    // (sessionStorage sekmeye özeldir; yeni sekme boş sessionStorage ile açılır)
    if (!rememberMe) {
      localStorage.setItem(key, value);
    }
  }

  private clearStoredAuth(): void {
    // Clear specific auth keys
    [localStorage, sessionStorage].forEach((storage) => {
      storage.removeItem(AUTH_TOKEN_KEY);
      storage.removeItem(REFRESH_TOKEN_KEY);
      storage.removeItem(USER_KEY);
    });

    // Clear all sessionStorage to ensure complete cleanup
    sessionStorage.clear();

    this.clearAuthCookies();
  }

  private getStoredValue(key: string): string | null {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  }

  private removeStoredValue(key: string): void {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }

  private setAuthCookies(data: LoginResponseDto): void {
    const sessionExpiration = data.refreshTokenExpired ?? data.expiration;

    this.setCookie(ACCESS_TOKEN_COOKIE, data.accessToken, sessionExpiration);

    // Boş refreshToken cookie'yi siler; API alan göndermediyse eskisini koru
    if (data.refreshToken) {
      this.setCookie(REFRESH_TOKEN_COOKIE, data.refreshToken, sessionExpiration);
    }

    // Access expiration'ı refresh expiry yerine yazma — refresh erken "dolmuş" sayılır
    if (data.refreshTokenExpired) {
      this.setCookie(
        REFRESH_TOKEN_EXPIRES_COOKIE,
        this.stringifyCookieDate(data.refreshTokenExpired),
        sessionExpiration
      );
    }

    if (data.expiration) {
      this.setCookie(
        TOKEN_EXPIRES_COOKIE,
        this.stringifyCookieDate(data.expiration),
        sessionExpiration
      );
    }

    if (data.email) {
      this.setCookie(EMAIL_COOKIE, data.email, sessionExpiration);
    }

    // Login olduktan sonra aktif dili cookie'ye kaydet
    const currentLanguage = this.translateService.getCurrentLanguage();
    this.setLanguageCookie(currentLanguage, sessionExpiration);
  }

  private clearAuthCookies(): void {
    // LANGUAGE + ACCOUNT_APP_URL logout sonrası yönlendirme / dil için kalır.
    // AUTH_LOGOUT_BEACON_COOKIE bilinçli bırakılır (diğer origin sekmeleri okusun).
    [ACCESS_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE, REFRESH_TOKEN_EXPIRES_COOKIE, TOKEN_EXPIRES_COOKIE, EMAIL_COOKIE].forEach(
      (name) => this.deleteCookie(name)
    );
  }

  /**
   * Dil bilgisini cookie'ye kaydeder
   * @param language - Dil kodu
   * @param expiration - Cookie expiration (opsiyonel, varsayılan 1 yıl)
   */
  public setLanguageCookie(language: string, expiration?: string | Date | null): void {
    if (!language || typeof language !== 'string') {
      return;
    }

    // Expiration yoksa 1 yıl sonrasına ayarla
    let expiresDate: Date;
    if (expiration) {
      expiresDate = typeof expiration === 'string' ? new Date(expiration) : expiration;
    } else {
      expiresDate = new Date();
      expiresDate.setFullYear(expiresDate.getFullYear() + 1);
    }

    this.setCookie(LANGUAGE_COOKIE, language.toLowerCase(), expiresDate);
  }

  /**
   * URL parametrelerinden auth bilgilerini alıp cookie'lere yazar
   * Clinic gibi diğer uygulamalarda kullanım için public
   * @param queryParams - ActivatedRoute'dan alınan query params (opsiyonel)
   * @returns URL'de auth parametreleri varsa Observable<boolean>, yoksa Observable<false>
   */
  persistAuthParamsFromUrl(queryParams?: Record<string, string | null>): Observable<boolean> {
    // Query params verilmişse onları kullan, yoksa window.location'dan oku
    let accessToken: string | null;
    let refreshToken: string | null;
    let refreshTokenExpired: string | null;
    let expiration: string | null;
    let email: string | null;
    let language: string | null;
    let accountAppUrl: string | null;

    if (queryParams && Object.keys(queryParams).length > 0) {
      // ActivatedRoute'dan gelen query params'ı kullan
      accessToken = queryParams['accessToken'] || null;
      refreshToken = queryParams['refreshToken'] || null;
      refreshTokenExpired = queryParams['refreshTokenExpired'] || null;
      expiration = queryParams['expiration'] || null;
      email = queryParams['email'] || null;
      language = queryParams['language'] || null;
      accountAppUrl = queryParams['accountAppUrl'] || null;
    } else {
      // Fallback: window.location'dan oku (hem search hem hash)
      if (typeof window === 'undefined') {
        return of(false);
      }

      const searchParams = new URLSearchParams(window.location.search);
      const hashParts = window.location.hash.split('?');
      const hashParams = hashParts.length > 1 ? new URLSearchParams(hashParts[1]) : new URLSearchParams();

      accessToken = searchParams.get('accessToken') || hashParams.get('accessToken');
      refreshToken = searchParams.get('refreshToken') || hashParams.get('refreshToken');
      refreshTokenExpired = searchParams.get('refreshTokenExpired') || hashParams.get('refreshTokenExpired');
      expiration = searchParams.get('expiration') || hashParams.get('expiration');
      email = searchParams.get('email') || hashParams.get('email');
      language = searchParams.get('language') || hashParams.get('language');
      accountAppUrl = searchParams.get('accountAppUrl') || hashParams.get('accountAppUrl');
    }


    // If we have auth params in URL, write them to cookies and storage
    if (accessToken && refreshToken) {
      // Eski in-flight refresh yeni handoff oturumunu login?logout=true ile düşürmesin
      this.bumpSessionEpoch('URL handoff');
      this.beginForceLogoutHandoffGrace();

      // --- YENİ OTURUMU BELLEKTE ZORLA GEÇERLİ KIL ---
      // Eski oturum izlerini memory'den (constructorda yüklenenler) silip 
      // parametrelerdeki güncel tokenleri yerleştiriyoruz. 
      // Böylece Auth/Me veya Auth/RefreshToken çağrıları güncel bilgileri kullanarak atılır.
      this._token.set(accessToken);
      const tempUser = new LoginResponseDto();
      tempUser.email = email || '';
      tempUser.accessToken = accessToken;
      tempUser.refreshToken = refreshToken;
      tempUser.expiration = expiration || undefined;
      if (refreshTokenExpired) {
        tempUser.refreshTokenExpired = new Date(refreshTokenExpired);
      }
      this._user.set(tempUser);
      this._isAuthenticated.set(true);
      // ---------------------------------------------

      const sessionExpiration = refreshTokenExpired || expiration;

      // Cookie'lere yaz
      this.setCookie(ACCESS_TOKEN_COOKIE, accessToken, sessionExpiration);
      // RefreshToken'ı cookie'ye yaz
      this.setCookie(REFRESH_TOKEN_COOKIE, refreshToken, sessionExpiration);

      // Access vs refresh expiry karıştırma: access dolunca refresh'i de dolmuş saymayalım
      if (refreshTokenExpired) {
        this.setCookie(REFRESH_TOKEN_EXPIRES_COOKIE, refreshTokenExpired, sessionExpiration);
      }
      if (expiration) {
        this.setCookie(TOKEN_EXPIRES_COOKIE, expiration, sessionExpiration);
      }

      if (email) {
        this.setCookie(EMAIL_COOKIE, email, sessionExpiration);
      }

      if (accountAppUrl) {
        this.setCookie(ACCOUNT_APP_URL_COOKIE, accountAppUrl, sessionExpiration);
      }

      // Cookie yazıldıktan sonra — sessionHadSharedCookies doğru olsun
      this.markLocalSessionActive(true);

      // Storage'lara da yaz (expiration varsa ve gelecekte bir tarihse localStorage, yoksa sessionStorage)
      if (typeof window !== 'undefined') {
        let rememberMe = false; // Varsayılan olarak sessionStorage kullan
        if (expiration) {
          const expirationDate = new Date(expiration);
          if (!isNaN(expirationDate.getTime()) && expirationDate.getTime() > Date.now()) {
            rememberMe = true; // Expiration gelecekte bir tarihse localStorage kullan
          }
        }

        // AccessToken'ı storage'a yaz
        this.setSessionValue(AUTH_TOKEN_KEY, accessToken, rememberMe);

        // RefreshToken'ı storage'a yaz
        this.setSessionValue(REFRESH_TOKEN_KEY, refreshToken, rememberMe);

        // User bilgisini de storage'a yaz (eğer email varsa basit bir user objesi oluştur)
        if (email) {
          const userData = {
            accessToken,
            refreshToken,
            email,
            expiration,
            refreshTokenExpired
          };
          this.setSessionValue(USER_KEY, JSON.stringify(userData), rememberMe);
        }
      }

      // Dil bilgisini cookie'ye kaydet ve TranslateService'e uygula
      if (language) {
        this.setLanguageCookie(language, refreshTokenExpired || expiration);
        this.translateService.setLanguage(language).catch(err => {
          console.warn('[Auth] URL’den dil ayarlanamadı:', err);
        });
      }

      // Remove query params from URL after processing
      // Bu işlem queryParams verilse de verilmese de çalışmalı
      // Hem query string'den hem de hash içindeki query parametrelerinden temizle
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const authParams = AUTH_PARAM_KEYS;
        let hasChanges = false;

        // Query string'den parametreleri temizle
        authParams.forEach(param => {
          if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
            hasChanges = true;
          }
        });

        // Hash içindeki query parametrelerini kontrol et ve temizle
        if (url.hash && url.hash.includes('?')) {
          const hashParts = url.hash.split('?');
          if (hashParts.length === 2) {
            const hashParams = new URLSearchParams(hashParts[1]);
            let hashHasChanges = false;

            authParams.forEach(param => {
              if (hashParams.has(param)) {
                hashParams.delete(param);
                hashHasChanges = true;
              }
            });

            if (hashHasChanges) {
              const newHashParams = hashParams.toString();
              url.hash = hashParts[0] + (newHashParams ? `?${newHashParams}` : '');
              hasChanges = true;
            }
          }
        }

        if (hasChanges) {
          window.history.replaceState({}, '', url.toString());
        }
      }

      // Access dolmuşsa /Me değil /RefreshToken yolu (aksi halde clearAuth + logout)
      if (this.isTokenExpired()) {
        return this.ensureValidToken().pipe(map((token) => !!token));
      }
      return this.refreshSession(accessToken);
    }

    // Eğer sadece language parametresi varsa (auth params yoksa), onu da işle ve URL'den temizle
    if (language && typeof window !== 'undefined') {
      this.setLanguageCookie(language);
      this.translateService.setLanguage(language).catch(err => {
        console.warn('[Auth] URL’den dil ayarlanamadı:', err);
      });

      // Language parametresini URL'den temizle (hem query string'den hem de hash'ten)
      this.removeLanguageParamFromUrl();
    }

    return of(false);
  }

  /**
   * URL'den language parametresini temizler (hem query string'den hem de hash'ten)
   */
  private removeLanguageParamFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    let hasChanges = false;

    // Query string'den language parametresini temizle
    if (url.searchParams.has('language')) {
      url.searchParams.delete('language');
      hasChanges = true;
    }

    // Hash içindeki query parametrelerini kontrol et ve temizle
    if (url.hash && url.hash.includes('?')) {
      const hashParts = url.hash.split('?');
      if (hashParts.length === 2) {
        const hashParams = new URLSearchParams(hashParts[1]);
        if (hashParams.has('language')) {
          hashParams.delete('language');
          const newHashParams = hashParams.toString();
          url.hash = hashParts[0] + (newHashParams ? `?${newHashParams}` : '');
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      window.history.replaceState({}, '', url.toString());
    }
  }



  /**
   * Cookie'lerde access ve refresh token olup olmadığını kontrol eder.
   * Login component gibi yerlerde doğrudan cookie okumak yerine bu method kullanılmalıdır.
   */
  hasAuthCookies(): boolean {
    const accessToken = this.getCookie(ACCESS_TOKEN_COOKIE);
    const refreshToken = this.getCookie(REFRESH_TOKEN_COOKIE);
    return !!(accessToken && refreshToken);
  }

  /**
   * Cookie'lerden veya storage'dan session'ı restore etmeye çalışır.
   * Eğer accessToken süresi dolmuşsa refreshToken ile yenilemeyi dener.
   * Clinic gibi diğer uygulamalarda kullanım için public.
   * @returns Session başarıyla restore edildiyse true, aksi halde false
   */
  tryRestoreSessionFromCookies(): Observable<boolean> {
    // Önce cookie'den accessToken'ı kontrol et
    let accessToken = this.getCookie(ACCESS_TOKEN_COOKIE);

    // Cookie'de yoksa localStorage'dan kontrol et
    if (!accessToken && typeof window !== 'undefined') {
      accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
    }

    // localStorage'ta da yoksa sessionStorage'dan kontrol et
    if (!accessToken && typeof window !== 'undefined') {
      accessToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
    }

    if (!accessToken) {
      return of(false);
    }

    // Eğer _user henüz yüklenmemişse, storage'dan yükle
    // (ensureValidToken email ve refreshToken'a ihtiyaç duyar)
    if (!this._user()) {
      const user = this.loadStoredUser();
      if (user) {
        this._user.set(user);
        this._token.set(accessToken);
      }
    }

    // Token süresini kontrol et. Dolmuşsa /RefreshToken ile yenile, dolmamışsa /Me ile doğrula.
    if (this.isTokenExpired()) {
      // Token süresi dolmuş, ensureValidToken refreshToken ile yenilemeyi dener
      return this.ensureValidToken().pipe(
        map((token) => {
          if (token) {
            this.scheduleSilentRefresh();
            return true;
          }
          return false;
        }),
        catchError((err) => {
          this.logSessionEnd(AUTH_SESSION_END_EXPIRED, `oturum geri yükleme hatası: ${err}`);
          this.clearAuth(true, AUTH_SESSION_END_EXPIRED);
          return of(false);
        })
      );
    }

    // Token hala geçerli, /Me endpoint'i ile doğrula
    return this.refreshSession(accessToken).pipe(
      tap((success) => {
        if (success) {
          this.scheduleSilentRefresh();
        }
      })
    );
  }

  /**
   * Auth durumunu kalıcı hale getirir (setAuthState için alias)
   * Clinic gibi diğer uygulamalarda kullanım için public
   * @param data - Login response data
   * @param rememberMe - Beni hatırla seçeneği
   */
  persistAuthState(data: LoginResponseDto, rememberMe: boolean): void {
    this.setAuthState(data, rememberMe);
  }

  /**
   * Cookie/storage'daki access token ile oturumu bootstrap eder.
   * Access dolmuşsa /Me değil RefreshToken kullanır (CAD gibi uzun oturumlarda logout'u önler).
   */
  private bootstrapSessionFromCookieOrStorage(): void {
    const accessToken =
      this.getCookie(ACCESS_TOKEN_COOKIE) ??
      this.getStoredValue(AUTH_TOKEN_KEY);
    if (!accessToken) {
      return;
    }

    if (!this._user()) {
      const user = this.loadStoredUser();
      if (user) {
        this._user.set(user);
        this._token.set(accessToken);
      }
    } else if (!this._token()) {
      this._token.set(accessToken);
    }

    if (this.isTokenExpired()) {
      this.ensureValidToken().subscribe();
      return;
    }

    this.refreshSession(accessToken).subscribe();
  }

  public refreshSession(accessToken: string): Observable<boolean> {
    if (!accessToken) {
      // Boş refresh token ile çağrılmışsa, sadece auth'u temizle
      // Navigate işlemini guard'lara bırak
      this.logSessionEnd(AUTH_SESSION_END_EXPIRED, 'oturum yenileme: erişim tokenı boş');
      this.clearAuth(true, AUTH_SESSION_END_EXPIRED);
      return of(false);
    }

    this._isLoading.set(true);


    // Create RefreshTokenModelDto for Auth/Me endpoint
    const refreshTokenModel = new RefreshTokenModelDto();
    refreshTokenModel.setData({ accessToken });

    return this.accountApiService
      .me(refreshTokenModel)
      .pipe(
        switchMap((response) => {
          if (response.success && response.data) {
            const rememberMe = this.resolveRememberMePreference();
            this.setAuthState(response.data, rememberMe);
            this.sessionWrittenViaMe = true;
            // Warm package cache once after Auth/Me (skip if already loaded).
            this.prefetchActiveSubscriptions(false);
            return of(true);
          }
          // /Me soft-fail: refresh token ile kurtarmayı dene, hemen clearAuth yapma
          if (!this.isRefreshTokenExpired()) {
            return this.ensureValidToken().pipe(map((token) => !!token));
          }
          this.logSessionEnd(AUTH_SESSION_END_EXPIRED, 'Auth/Me başarısız ve yenileme tokenı süresi dolmuş');
          this.clearAuth(true, AUTH_SESSION_END_EXPIRED);
          return of(false);
        }),
        catchError((error: unknown) => {
          // Sadece açık auth reddi (401/403) veya refresh token dolmuşsa auth'u temizle
          // Geçici ağ kopmalarında oturumu kapatma
          const isAuthRejection =
            error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
          if (isAuthRejection && !this.isRefreshTokenExpired()) {
            return this.ensureValidToken().pipe(map((token) => !!token));
          }
          if (isAuthRejection || this.isRefreshTokenExpired()) {
            const status = error instanceof HttpErrorResponse ? error.status : 'bilinmiyor';
            this.logSessionEnd(
              AUTH_SESSION_END_EXPIRED,
              `Auth/Me hatası status=${status}, refreshSuresiDolmus=${this.isRefreshTokenExpired()}`
            );
            this.clearAuth(true, AUTH_SESSION_END_EXPIRED);
          }
          return of(false);
        }),
        finalize(() => this._isLoading.set(false))
      );
  }

  showRedirectOverlay(mode: AuthRedirectMode, appName?: string): void {
    this.clearRedirectOverlayTimeout();
    this._redirectOverlay.set(mode);
    this._redirectOverlayApp.set(this.normalizeOverlayApp(appName));
    if (!this._redirectOverlaySourceApp()) {
      this._redirectOverlaySourceApp.set(this.detectCurrentAppName());
    }

    const timeoutMs = mode === AUTH_REDIRECT_MODE_MODULE ? 4000 : 15000;
    this.redirectOverlayTimeout = setTimeout(() => {
      if (this._redirectOverlay() === mode) {
        this.hideRedirectOverlay();
      }
    }, timeoutMs);
  }

  hideRedirectOverlay(): void {
    this.clearRedirectOverlayTimeout();
    this._redirectOverlay.set(null);
    this._redirectOverlayApp.set(null);
    this._redirectOverlaySourceApp.set(null);
  }

  private normalizeOverlayApp(appName?: string | null): string | null {
    const name = (appName || '').trim().toLowerCase();
    return name || null;
  }

  private detectCurrentAppName(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const hostname = window.location.hostname.toLowerCase();
    const port = window.location.port;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      for (const [appName, config] of Object.entries(APP_CONFIGS)) {
        if (config?.devPort && port === String(config.devPort)) {
          return appName;
        }
      }
      return null;
    }

    const subdomain = hostname.split('.')[0]?.replace(/^dev-/, '') ?? '';
    return APP_CONFIGS[subdomain] ? subdomain : null;
  }

  private clearRedirectOverlayTimeout(): void {
    if (this.redirectOverlayTimeout !== undefined) {
      clearTimeout(this.redirectOverlayTimeout);
      this.redirectOverlayTimeout = undefined;
    }
  }

  /**
   * Uygulamalar arası yönlendirme; modül geçişlerinde overlay gösterir.
   * Login/logout akışları kendi overlay'lerini zaten ayarladığı için appNavigation doğrudan kullanılır.
   */
  navigateToApp(appName: string, path = '', openInNewTab = false, showModuleOverlay = true): void {
    if (!this.appNavigation) {
      return;
    }

    const shouldShowOverlay =
      showModuleOverlay &&
      !openInNewTab &&
      appName !== 'landing' &&
      !this.redirectOverlay();

    if (shouldShowOverlay) {
      this.showRedirectOverlay('module', appName);
      setTimeout(() => this.appNavigation?.navigateToApp(appName, path, openInNewTab), 100);
      return;
    }

    if (this.redirectOverlay() && appName) {
      this._redirectOverlayApp.set(this.normalizeOverlayApp(appName));
    }

    this.appNavigation.navigateToApp(appName, path, openInNewTab);
  }

  getRedirectLanguage(): 'tr' | 'en' {
    const lang = this.translateService.getCurrentLanguage()?.toLowerCase();
    return lang === 'tr' ? 'tr' : 'en';
  }

  /**
   * Aktif abonelikleri GetAllSubscriptionByFilter ile yükler (CAD `isExportable` dahil).
   * Login / Auth/Me sonrası cache dolar; sonraki çağrılar API'ye gitmez (forceRefresh hariç).
   */
  public loadActiveSubscriptions(forceRefresh = false): Observable<SubscriptionDto[]> {
    if (this._activeSubscriptions !== null && !forceRefresh) {
      return of(this._activeSubscriptions);
    }

    if (this.subscriptionsLoadInFlight && !forceRefresh) {
      return this.subscriptionsLoadInFlight;
    }

    const filterDto = new SubscriptionFilterRequestDto();
    filterDto.setData({
      packageName: '',
      featureName: '',
      organizationName: '',
      status: 1, // Active subscriptions
      startDate: null,
      endDate: null,
      cancelDate: null,
    });

    const request = new SubscriptionGetAllByFilterRequest();
    request.setData({
      PaginationRequestModel: {
        Page: 1,
        NumberOfRecord: 50,
        SortColumn: '',
        SortOrder: '',
      },
      SubscriptionFilterRequestDto: filterDto,
    });

    const request$ = this.accountApiService.getActiveSubscriptions(request).pipe(
      map((response) => {
        const rawList = response?.data?.data || [];
        const subscriptions = rawList.map((item) => {
          const dto = new SubscriptionDto();
          dto.setData(item as Partial<SubscriptionDto> | Record<string, unknown>);
          return dto;
        });
        this.applyActiveSubscriptions(subscriptions);
        return subscriptions;
      }),
      catchError((error) => {
        console.warn('[Auth] Kullanıcı abonelikleri yüklenemedi:', error);
        this.applyActiveSubscriptions([]);
        return of([]);
      }),
      finalize(() => {
        if (this.subscriptionsLoadInFlight === request$) {
          this.subscriptionsLoadInFlight = null;
        }
      }),
      shareReplay(1)
    );

    this.subscriptionsLoadInFlight = request$;
    return request$;
  }

  /** Fire-and-forget warm of the active-subscription cache. */
  private prefetchActiveSubscriptions(forceRefresh: boolean): void {
    this.loadActiveSubscriptions(forceRefresh).subscribe({
      error: (err) => console.warn('[Auth] Abonelik önbelleği ısıtılamadı:', err),
    });
  }

  /** Force-refresh package cache (after Subscribe / Cancel / seat changes). */
  public refreshActiveSubscriptions(): Observable<SubscriptionDto[]> {
    return this.loadActiveSubscriptions(true);
  }

  /**
   * Apply Subscribe / GetAll payload into the cache when it looks like subscription DTO(s).
   * @returns true if cache was updated from `data`
   */
  public tryApplySubscriptionsFromApiData(data: unknown): boolean {
    const list = this.normalizeSubscriptionList(data);
    if (!list) {
      return false;
    }
    this.applyActiveSubscriptions(list);
    return true;
  }

  /**
   * Merge a single subscription from Subscribe/update into the active cache.
   */
  public mergeActiveSubscription(
    raw: Partial<SubscriptionDto> | Record<string, unknown>
  ): void {
    const dto = new SubscriptionDto();
    dto.setData(raw);
    const current = [...(this._activeSubscriptions ?? [])];
    const idx = current.findIndex((s) => !!s.id && s.id === dto.id);
    if (idx >= 0) {
      current[idx] = dto;
    } else {
      current.push(dto);
    }
    this.applyActiveSubscriptions(current);
  }

  private normalizeSubscriptionList(data: unknown): SubscriptionDto[] | null {
    if (data == null) {
      return null;
    }
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return [];
      }
      return data.map((item) => {
        const dto = new SubscriptionDto();
        dto.setData(item as Partial<SubscriptionDto> | Record<string, unknown>);
        return dto;
      });
    }
    if (typeof data !== 'object') {
      return null;
    }
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj[SUBSCRIPTION_DTO_KEY_DATA])) {
      return this.normalizeSubscriptionList(obj[SUBSCRIPTION_DTO_KEY_DATA]);
    }
    // Single subscription DTO
    if (
      SUBSCRIPTION_DTO_KEY_PACKAGE_NAME in obj ||
      SUBSCRIPTION_DTO_KEY_PACKAGE_NAME_PASCAL in obj ||
      SUBSCRIPTION_DTO_KEY_ID in obj ||
      SUBSCRIPTION_DTO_KEY_ID_PASCAL in obj
    ) {
      const dto = new SubscriptionDto();
      dto.setData(obj);
      const current = [...(this._activeSubscriptions ?? [])];
      const idx = current.findIndex((s) => !!s.id && s.id === dto.id);
      if (idx >= 0) {
        current[idx] = dto;
      } else {
        current.push(dto);
      }
      return current;
    }
    return null;
  }

  /**
   * Kullanıcının aktif aboneliklerine ait uygulama isimlerini getirir (clinic, lab, cad)
   */
  public getSubscribedAppNames(forceRefresh = false): Observable<string[]> {
    if (this._subscribedApps !== null && !forceRefresh) {
      return of(this._subscribedApps);
    }

    return this.loadActiveSubscriptions(forceRefresh).pipe(
      map(() => this._subscribedApps ?? [])
    );
  }

  private applyActiveSubscriptions(subscriptions: SubscriptionDto[]): void {
    this._activeSubscriptions = subscriptions;
    this._activeSubscriptionsSignal.set(subscriptions);
    this._cadExportable.set(
      this.resolveCadPackageFlag(subscriptions, (sub) => sub.isExportable === true)
    );
    this._cadShareable.set(
      this.resolveCadPackageFlag(subscriptions, (sub) => sub.isShareable === true)
    );

    const appNames = new Set<string>();
    subscriptions.forEach((sub: SubscriptionDto) => {
      if (sub.packageName && sub.status === 1) {
        const appName = mapPackageNameToAppName(sub.packageName);
        if (appName) {
          appNames.add(appName);
        }
      }
    });
    const result = Array.from(appNames);
    // Lab aboneliği olan kullanıcı CAD uygulamasını da kullanabilir
    if (appNames.has(LAB_APP_NAME) && !appNames.has(CAD_APP_NAME)) {
      result.push(CAD_APP_NAME);
    }
    this._subscribedApps = result;
  }

  /**
   * Prefer active CAD packages; fall back to Lab when no explicit CAD package.
   * Flag is true if any package in the pool matches (e.g. isExportable / isShareable).
   */
  private resolveCadPackageFlag(
    subscriptions: SubscriptionDto[],
    flag: (sub: SubscriptionDto) => boolean
  ): boolean {
    const active = subscriptions.filter((sub) => sub.status === 1);
    const cadPackages = active.filter((sub) => sub.isCad === true);
    const pool =
      cadPackages.length > 0 ? cadPackages : active.filter((sub) => sub.isLab === true);
    if (pool.length === 0) {
      return false;
    }
    return pool.some(flag);
  }

  /**
   * Kullanıcının belirtilen uygulamaya erişim yetkisinin olup olmadığını kontrol eder
   */
  public hasAppAccess(appName: string): Observable<boolean> {
    if (!appName || appName === 'account' || appName === 'landing') {
      return of(true);
    }
    const user = this._user();
    const isAdmin = Boolean(user?.isAdmin);
    if (isAdmin) {
      return of(true);
    }
    if (appName === 'portal') {
      return of(false);
    }
    return this.getSubscribedAppNames().pipe(
      map((apps) => apps.includes(appName) || (appName === CAD_APP_NAME && apps.includes(LAB_APP_NAME)))
    );
  }

  /**
   * Belirtilen uygulama için "Open Application" sürecini çalıştırır.
   * Gerekirse userSettings.appType değerini günceller ve hedef uygulamaya yönlendirir.
   */
  /** Lab home: `/main` is development-only; release hosts use `/design-projects`. */
  private resolveLabEntryPath(): string {
    return isDevelopmentBuild() ? LAB_ENTRY_PATH_DEV : LAB_ENTRY_PATH_RELEASE;
  }

  public openApplication(appName: string, path = ''): void {
    // Live hosts only: clinic is not released — send users to Lab instead.
    // Dev / local: clinic stays open (redirect and manual open allowed).
    if (appName === CLINIC_APP_NAME && !isDevelopmentBuild()) {
      this.openApplication(LAB_APP_NAME, this.resolveLabEntryPath());
      return;
    }

    // CAD / Autobar requires an assigned Designer seat (isDesignBar) — otherwise Lab.
    if (appName === CAD_APP_NAME && !this.hasDesignBarAccess()) {
      console.warn('[Auth] CAD blocked: isDesignBar is not true — redirecting to Lab');
      this.openApplication(LAB_APP_NAME, this.resolveLabEntryPath());
      return;
    }

    const user = this._user();
    const appType = getAppTypeByName(appName);

    let finalPath = path;
    if (!finalPath && appName === LAB_APP_NAME) {
      finalPath = this.resolveLabEntryPath();
    } else if (!finalPath && (appName === CLINIC_APP_NAME || appName === CAD_APP_NAME)) {
      finalPath = APP_ENTRY_PATH_MAIN;
    } else if (!finalPath && appName === ACCOUNT_APP_NAME) {
      // Empty path hits account '' → /login → loginGuard → Lab bounce for lab roles.
      finalPath = ACCOUNT_ENTRY_PATH;
    }

    if (appType > 0 && user) {
      const currentAppType = user.userSettingsDto?.appType ?? 0;
      if (currentAppType !== appType) {
        const settingsPayload = new UserSettingsDto();
        settingsPayload.setData({
          id: user.userSettingsDto?.id ?? '',
          userId: user.userId || '',
          dateFormat: user.userSettingsDto?.dateFormat ?? 'dd.MM.yyyy',
          dentalNotation: user.userSettingsDto?.dentalNotation ?? 'FDI',
          fileFormat: user.userSettingsDto?.fileFormat ?? '.stl',
          language: user.userSettingsDto?.language ?? 'EN',
          appType: appType,
          mouseControl: user.userSettingsDto?.mouseControl,
        });

        const updateCall$ = user.userSettingsDto?.id
          ? this.accountApiService.updateUserSettings(settingsPayload, true)
          : this.accountApiService.createUserSettings(settingsPayload, true);

        updateCall$.subscribe({
          next: (response) => {
            if (response.success && response.data) {
              if (user.userSettingsDto) {
                user.userSettingsDto.appType = appType;
              }
            }
          },
          error: (err) => console.warn('[Auth] Kullanıcı ayarları uygulama tipi güncellenemedi:', err),
        });
      }
    }

    if (this.appNavigation) {
      this.navigateToApp(appName, finalPath, false);
    } else {
      this.router.navigate(['/account']);
    }
  }

  /**
   * Başarılı login veya aktif oturum sonrası yönlendirme.
   * Admin kullanıcılar her durumda portal'a gider; diğerleri için redirectTestUrl, redirect, Lab.
   * @param queryParams - URL'den gelen query parametreleri
   * @param isPartnerInviteAccepted - Partner davetinin kabul edilip edilmediği (isteğe bağlı)
   */
  public navigateAfterLogin(queryParams: Params, isPartnerInviteAccepted = false): void {
    const user = this._user();
    if (!user) {
      this.router.navigate(['/login']);
      return;
    }

    this.showRedirectOverlay('login');

    // Admin kullanıcılar her durumda portal uygulamasına yönlendirilir
    // (redirect / redirectTestUrl / appType / partner invite fark etmeksizin).
    if (user.isAdmin === true) {
      if (this.appNavigation) {
        this.navigateToApp('portal', '', false);
      } else {
        this.router.navigate(['/account']);
      }
      return;
    }

    // Ortak token parametrelerini oluştur
    const authParams = new URLSearchParams();
    if (user.accessToken) authParams.append('accessToken', user.accessToken);
    if (user.refreshToken) authParams.append('refreshToken', user.refreshToken);
    if (user.refreshTokenExpired) {
      const expiredStr = user.refreshTokenExpired instanceof Date
        ? user.refreshTokenExpired.toISOString()
        : String(user.refreshTokenExpired);
      authParams.append('refreshTokenExpired', expiredStr);
    }
    if (user.expiration) authParams.append('expiration', user.expiration);
    if (user.email) authParams.append('email', user.email);

    const currentLang = this.translateService.getCurrentLanguage();
    if (currentLang) authParams.append('language', currentLang);

    if (typeof window !== 'undefined') {
      authParams.append('accountAppUrl', window.location.origin + window.location.pathname);
    }

    // redirectTestUrl: External URL'e token bilgileriyle yönlendirme (test ortamları için)
    const redirectTestUrl = queryParams['redirectTestUrl'];
    if (redirectTestUrl) {
      let targetUrl = redirectTestUrl;
      // Hash yoksa ve Angular HashLocationStrategy varsayımıyla #/ ekleyelim
      if (!targetUrl.includes('#')) {
        if (!targetUrl.endsWith('/')) {
          targetUrl += '/';
        }
        targetUrl += '#/';
      }

      const separator = targetUrl.includes('?') ? '&' : '?';
      if (typeof window !== 'undefined') {
        window.location.href = `${targetUrl}${separator}${authParams.toString()}`;
        return;
      }
    }

    const redirectParam = queryParams['redirect'];
    if (redirectParam && this.appNavigation) {
      const colonIndex = redirectParam.indexOf(':');
      const appName = colonIndex !== -1 ? redirectParam.substring(0, colonIndex) : redirectParam;
      const targetPath = colonIndex !== -1 ? redirectParam.substring(colonIndex + 1) : '';

      // Always respect role: non-clinic roles cannot follow redirect=clinic.
      const clinicRedirectBlocked =
        appName === CLINIC_APP_NAME && !canAccessClinicByRole(user.role);

      if (!clinicRedirectBlocked && this.appNavigation.isAppEnabled(appName)) {
        this.openApplication(appName, targetPath);
        return;
      }
      // Fall through to role-based routing when clinic redirect is blocked.
    }

    if (isAccountApp()) {
      if (isPartnerInviteAccepted) {
        this.router.navigate(['/account/partners']);
        return;
      }

      // Always land on Lab after account login / first-time activation (not /account).
      this.openApplication(LAB_APP_NAME, this.resolveLabEntryPath());
    } else {
      this.openApplication(LAB_APP_NAME, this.resolveLabEntryPath());
    }
  }

  /**
   * Account login sayfasına yönlendirir
   * Uygulama tipine göre farklı yönlendirme yapar
   */
  private navigateToAccountLogin(): void {
    // clearAuth cookie'leri silmeden önce account URL'ini sakla
    const accountAppUrl = this.getCookie(ACCOUNT_APP_URL_COOKIE);
    this.clearAuth(false);
    this.remoteLogoutInProgress = false;

    // Eğer account uygulamasındaysak doğrudan /login'e yönlendir
    if (isAccountApp() || !this.appNavigation) {
      // Already on account: no cross-app wait — do not leave logout overlay stuck on /login.
      this.hideRedirectOverlay();
      this.router.navigate(['/login'], { queryParams: { logout: 'true' } });
      return;
    }

    this.showRedirectOverlay('logout');

    if (accountAppUrl && typeof window !== 'undefined') {
      try {
        const origin = new URL(accountAppUrl).origin;
        const loginUrl = new URL('#/login?logout=true', origin);
        window.location.href = loginUrl.toString();
        return;
      } catch {
        // Fall through to default navigation.
      }
    }

    // Clinic gibi diğer uygulamalarda account app'e yönlendir
    // logout=true query parametresi ekle ki login guard oturumu temizlesin
    this.appNavigation.navigateToApp('account', '/login?logout=true');
  }

  private resolveRememberMePreference(): boolean {
    return Boolean(localStorage.getItem(AUTH_TOKEN_KEY));
  }

  private loadStoredUser(): loginUser | null {
    const userStr = this.getStoredValue(USER_KEY);
    if (!userStr) {
      return null;
    }

    try {
      const parsed = JSON.parse(userStr);

      // Convert plain object to LoginResponseDto instance to ensure all properties are available
      const user = new LoginResponseDto();
      user.setData(parsed);

      return user;
    } catch (error: unknown) {
      console.error('[Auth] Kayıtlı kullanıcı verisi çözümlenemedi:', error);
      return null;
    }
  }

  private toStringParam(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) return value[0] ?? null;
    return typeof value === 'string' ? value : null;
  }

  private setCookie(
    name: string,
    value: string,
    expires?: string | Date | null
  ): void {
    if (!this.document) {
      return;
    }

    if (!value) {
      this.deleteCookie(name);
      return;
    }

    const expiresDate = this.parseToDate(expires);
    let cookie = `${name}=${encodeURIComponent(value)}; path=/;`;
    const domain = this.resolveCookieDomain();
    if (domain) {
      cookie += ` domain=${domain};`;
    }
    if (expiresDate) {
      cookie += ` expires=${expiresDate.toUTCString()};`;
    }
    cookie += ' SameSite=Lax;';
    if (this.isSecureContext()) {
      cookie += ' Secure;';
    }
    this.document.cookie = cookie;
  }

  private deleteCookie(name: string): void {
    if (!this.document) {
      return;
    }
    let cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    const domain = this.resolveCookieDomain();
    if (domain) {
      cookie += ` domain=${domain};`;
    }
    this.document.cookie = cookie;
  }

  private getCookie(name: string): string | null {
    if (!this.document) {
      return null;
    }
    const cookies = this.document.cookie?.split('; ') ?? [];
    for (const cookie of cookies) {
      if (cookie.startsWith(`${name}=`)) {
        return decodeURIComponent(cookie.substring(name.length + 1));
      }
    }
    return null;
  }

  private parseToDate(input?: string | Date | null): Date | null {
    if (!input) {
      return null;
    }
    if (input instanceof Date) {
      return input;
    }
    const parsed = new Date(input);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private stringifyCookieDate(input?: string | Date | null): string {
    const date = this.parseToDate(input);
    return date ? date.toISOString() : '';
  }

  private resolveCookieDomain(): string | undefined {
    if (typeof location === 'undefined') {
      return undefined;
    }

    const hostname = location.hostname;

    // 1. Localhost Durumları (Geliştirme)
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
      return hostname.endsWith('.localhost') ? '.localhost' : undefined;
    }

    // 2. Paylaşılabilir Standart Domainler (Production & Shared Dev)
    // Örn: portal.autobar.design veya dev-portal.autobar.design -> .autobar.design
    if (hostname.endsWith('.autobar.design')) {
      return '.autobar.design';
    }

    // 3. Dev-Preview & Bulut Domainleri (Host-only)
    // Örn: dev-123.azurestaticapps.net vb.
    if (hostname.startsWith('dev-') || hostname.includes('azurestaticapps.net')) {
      return undefined;
    }

    // 4. Fallback (Bilinmeyen diğer domainler)
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const baseDomain = parts.slice(-2).join('.');
      return `.${baseDomain}`;
    }

    return undefined;
  }

  private isSecureContext(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.isSecureContext ?? window.location.protocol === 'https:';
  }

  /**
   * BroadcastChannel ile diğer sekmelere logout mesajı gönderir.
   * Ayrıca paylaşılan logout beacon cookie yazar (farklı origin: lab↔cad).
   */
  private broadcastLogout(reason: string = AUTH_SESSION_END_USER_LOGOUT): void {
    this.writeLogoutBeacon(reason);
    if (this.broadcastChannel) {
      try {
        const timestamp = Date.now();
        this.lastBroadcastTimestamp = timestamp;
        this.broadcastChannel.postMessage({
          type: LOGOUT_EVENT,
          timestamp,
          reason,
        });
      } catch (error) {
        console.warn('[Auth] Çıkış yayını gönderilemedi:', error);
      }
    }
  }

  /**
   * BroadcastChannel ile diğer sekmelere login mesajı gönderir
   */
  private broadcastLogin(): void {
    this.clearLogoutBeacon();
    if (this.broadcastChannel) {
      try {
        const timestamp = Date.now();
        this.lastBroadcastTimestamp = timestamp;
        this.broadcastChannel.postMessage({
          type: LOGIN_EVENT,
          timestamp,
        });
      } catch (error) {
        console.warn('[Auth] Giriş yayını gönderilemedi:', error);
      }
    }
  }

  /**
   * Diğer sekmelerden gelen logout/login mesajlarını dinler
   */
  private setupBroadcastListener(): void {
    if (!this.broadcastChannel) {
      return;
    }

    this.broadcastChannel.onmessage = (event: MessageEvent) => {
      const { type, timestamp, reason } = event.data ?? {};

      // Kendi broadcast mesajımızı ignore et (sonsuz döngüyü önle)
      if (timestamp && Math.abs(timestamp - this.lastBroadcastTimestamp) < 100) {
        return;
      }

      this.ngZone.run(() => {
        if (type === LOGOUT_EVENT) {
          if (!this._isAuthenticated()) {
            return;
          }

          const logoutReason =
            typeof reason === 'string' && reason
              ? reason
              : AUTH_SESSION_END_USER_LOGOUT;

          this.handleRemoteSessionEnd(logoutReason, 'BroadcastChannel');
        } else if (type === LOGIN_EVENT) {
          // Başka bir sekmede login yapıldı, bu sekmeyi de güncelle
          if (!this._isAuthenticated()) {
            this.bootstrapSessionFromCookieOrStorage();
          }
        }
      });
    };
  }

  private setupStorageListener(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('storage', this.onStorageEvent);
  }

  /**
   * Login / restore sonrası cross-origin logout izlemeyi başlatır.
   * @param clearBeacon true: yeni giriş (beacon sil). false: storage restore (beacon korunur).
   */
  private markLocalSessionActive(clearBeacon: boolean): void {
    this.sessionStartedAtMs = Date.now();
    this.sessionHadSharedCookies = this.hasAuthCookies();
    this.remoteLogoutInProgress = false;
    if (clearBeacon) {
      this.clearLogoutBeacon();
    }
    this.startLogoutBeaconWatch();
  }

  private writeLogoutBeacon(reason: string): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + AUTH_LOGOUT_BEACON_TTL_DAYS);
    this.setCookie(
      AUTH_LOGOUT_BEACON_COOKIE,
      `${reason}${AUTH_LOGOUT_BEACON_VALUE_SEPARATOR}${Date.now()}`,
      expires
    );
  }

  private clearLogoutBeacon(): void {
    this.deleteCookie(AUTH_LOGOUT_BEACON_COOKIE);
  }

  private parseLogoutBeacon(): AuthLogoutBeacon | null {
    const raw = this.getCookie(AUTH_LOGOUT_BEACON_COOKIE);
    if (!raw) {
      return null;
    }
    const sep = raw.lastIndexOf(AUTH_LOGOUT_BEACON_VALUE_SEPARATOR);
    if (sep <= 0) {
      return null;
    }
    const reason = raw.slice(0, sep);
    const at = Number(raw.slice(sep + 1));
    if (!reason || !Number.isFinite(at)) {
      return null;
    }
    return { reason, at };
  }

  private startLogoutBeaconWatch(): void {
    this.stopLogoutBeaconWatch();
    if (typeof window === 'undefined') {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.logoutBeaconTimer = setInterval(() => {
        this.ngZone.run(() => this.applyRemoteLogoutIfNeeded());
      }, AUTH_LOGOUT_BEACON_POLL_MS);
    });
  }

  private stopLogoutBeaconWatch(): void {
    if (this.logoutBeaconTimer) {
      clearInterval(this.logoutBeaconTimer);
      this.logoutBeaconTimer = null;
    }
  }

  /**
   * Cross-origin: paylaşılan cookie beacon veya auth cookie silinmesi.
   * CAD (:4205) logout → Lab (:4204) BroadcastChannel alamaz; beacon ile kapanır.
   */
  private applyRemoteLogoutIfNeeded(): void {
    if (!this._isAuthenticated() || this.remoteLogoutInProgress) {
      return;
    }

    const beacon = this.parseLogoutBeacon();
    if (beacon && beacon.at >= this.sessionStartedAtMs) {
      this.handleRemoteSessionEnd(
        beacon.reason,
        'cross-origin logout beacon'
      );
      return;
    }

    if (
      this.sessionHadSharedCookies &&
      !this.getCookie(ACCESS_TOKEN_COOKIE) &&
      !this.getCookie(REFRESH_TOKEN_COOKIE)
    ) {
      this.handleRemoteSessionEnd(
        AUTH_SESSION_END_USER_LOGOUT,
        'paylaşılan auth cookie başka origin/sekmede silindi'
      );
    }
  }

  private handleRemoteSessionEnd(reason: string, detail: string): void {
    if (!this._isAuthenticated() || this.remoteLogoutInProgress) {
      return;
    }

    if (reason === AUTH_SESSION_END_FORCE_LOGOUT) {
      this.requestForceLogoutDialog();
      return;
    }

    this.remoteLogoutInProgress = true;
    const normalized =
      reason === AUTH_SESSION_END_EXPIRED
        ? AUTH_SESSION_END_EXPIRED
        : AUTH_SESSION_END_USER_LOGOUT;
    this.logSessionEnd(normalized, detail);
    // navigateToAccountLogin → clearAuth(false); beacon zaten gönderen tarafta
    this.navigateToAccountLogin();
  }

  /**
   * Token'ın süresinin dolup dolmadığını kontrol eder (cookie'deki expiration)
   * @returns Token süresi dolmuşsa true, dolmamışsa false
   */
  isTokenExpired(): boolean {
    // Cookie'den expiration değerini oku
    let expirationStr = this.getCookie(TOKEN_EXPIRES_COOKIE);

    // Cookie'de yoksa user signal'inden (storage'dan yüklenen) al
    if (!expirationStr) {
      expirationStr = this._user()?.expiration || null;
    }

    if (!expirationStr) {
      // Expiration bilgisi hiçbir yerde yoksa → token yok veya bilgi eksik
      // Token yoksa expired değildir (henüz oluşmamıştır), token varsa expired sayılır
      return !!this._token();
    }

    try {
      const expirationDate = new Date(expirationStr);

      // Geçersiz tarih kontrolü
      if (isNaN(expirationDate.getTime())) {
        console.warn('[Auth] Geçersiz token bitiş tarihi:', expirationStr);
        return true;
      }

      // Şu anki zaman ile karşılaştır
      // 10 saniye güvenlik marjı ekle (network gecikmesi için)
      const now = new Date();
      const bufferMs = 10000; // 10 saniye

      return expirationDate.getTime() - bufferMs <= now.getTime();
    } catch (error) {
      console.warn('[Auth] Token bitiş tarihi çözümlenemedi:', error);
      return true;
    }
  }

  /**
   * Refresh token'ın süresinin dolup dolmadığını kontrol eder (cookie'deki refreshTokenExpired)
   * @returns Refresh token süresi dolmuşsa true, dolmamışsa false
   */
  isRefreshTokenExpired(): boolean {
    let expirationStr: string | null = this.getCookie(REFRESH_TOKEN_EXPIRES_COOKIE);

    // Cookie'de yoksa user signal'inden (storage'dan yüklenen) al
    if (!expirationStr) {
      const userRefreshExpired = this._user()?.refreshTokenExpired;
      if (userRefreshExpired) {
        expirationStr = userRefreshExpired instanceof Date
          ? userRefreshExpired.toISOString()
          : String(userRefreshExpired);
      }
    }

    if (!expirationStr) {
      // Refresh token expiration bilgisi hiçbir yerde yoksa false dön ki refresh denensin
      return false;
    }

    try {
      const expirationDate = new Date(expirationStr);
      if (isNaN(expirationDate.getTime())) {
        return true;
      }
      const now = new Date();
      const bufferMs = 5000; // 5 saniye güvenlik marjı
      return expirationDate.getTime() - bufferMs <= now.getTime();
    } catch {
      return true;
    }
  }

  /**
   * Guard soft-fail: cookie/storage'daki oturumu belleğe alır (HTTP yok).
   * Refresh token dolmuşsa false döner.
   */
  reviveSessionFromStorage(): boolean {
    if (this.parseLogoutBeacon()) {
      console.warn('[Auth] Oturum canlandırma atlandı: logout beacon');
      this.clearStoredAuth();
      return false;
    }

    if (this.isRefreshTokenExpired()) {
      console.warn('[Auth] Oturum canlandırma atlandı: yenileme tokenı süresi dolmuş');
      return false;
    }

    const accessToken =
      this.getCookie(ACCESS_TOKEN_COOKIE) ?? this.getStoredValue(AUTH_TOKEN_KEY);
    const user = this._user() ?? this.loadStoredUser();

    if (!accessToken || !user) {
      console.warn('[Auth] Oturum canlandırma başarısız: token veya kullanıcı yok');
      return false;
    }

    this._token.set(accessToken);
    this._user.set(user);
    this._isAuthenticated.set(true);
    this.markLocalSessionActive(false);
    console.info('[Auth] Bellekteki oturum canlandırıldı', {
      userId: user.userId,
      accessSuresiDolmus: this.isTokenExpired(),
    });
    this.scheduleSilentRefresh(user.expiration);
    return true;
  }

  /**
   * Her request öncesi interceptor tarafından çağrılır.
   * Token geçerliyse döner; süresi dolmuşsa refresh token ile yeniler veya logout yapar.
   * Eşzamanlı isteklerde tek refresh çağrısı yapılır (shareReplay).
   * @returns Geçerli access token veya null (logout yapıldıysa / soft-fail)
   */
  ensureValidToken(): Observable<string | null> {
    // Token süresi dolmamışsa mevcut token'ı döndür
    if (!this.isTokenExpired()) {
      const token =
        this._token() ??
        this.getCookie(ACCESS_TOKEN_COOKIE) ??
        this.getStoredValue(AUTH_TOKEN_KEY);
      return of(token);
    }

    console.info('[Auth] Erişim tokenı süresi dolmuş — yenileme yolu seçildi');

    // Refresh token da dolmuşsa logout
    if (this.isRefreshTokenExpired()) {
      console.warn('[Auth] Yenileme tokenı süresi dolmuş → oturum kapatılıyor');
      this.handleExpiredAndLogout('token kontrolü: yenileme tokenı süresi dolmuş');
      return of(null);
    }

    // Refresh token ile yeni access token al
    if (this.refreshInProgress) {
      console.info('[Auth] Devam eden yenileme paylaşılıyor (token kontrolü)');
      return this.refreshInProgress;
    }

    this.refreshInProgress = this.doRefreshToken().pipe(
      finalize(() => {
        this.refreshInProgress = null;
      }),
      shareReplay(1)
    );
    return this.refreshInProgress;
  }

  /**
   * Sunucudan 401 hatası alındığında interceptor tarafından çağrılır.
   * Token süresi kontrolünü atlayarak doğrudan refresh token ile yenileme dener.
   * @returns Geçerli access token veya null (logout yapıldıysa / soft-fail)
   */
  forceRefreshToken(): Observable<string | null> {
    console.info('[Auth] Zorla token yenileme başlıyor');

    if (this.isRefreshTokenExpired()) {
      console.warn('[Auth] Yenileme tokenı süresi dolmuş → oturum kapatılıyor (zorla yenileme)');
      this.handleExpiredAndLogout('zorla yenileme: yenileme tokenı süresi dolmuş');
      return of(null);
    }

    if (this.refreshInProgress) {
      console.info('[Auth] Devam eden yenileme paylaşılıyor (zorla yenileme)');
      return this.refreshInProgress;
    }

    this.refreshInProgress = this.doRefreshToken().pipe(
      finalize(() => {
        this.refreshInProgress = null;
      }),
      shareReplay(1)
    );
    return this.refreshInProgress;
  }

  /**
   * /Auth/RefreshToken endpoint'ine POST atarak yeni access token alır
   */
  private doRefreshToken(): Observable<string | null> {
    const epochAtStart = this.sessionEpoch;
    const email =
      this.getCookie(EMAIL_COOKIE) ??
      this._user()?.email ??
      '';
    const accessToken =
      this._token() ??
      this.getCookie(ACCESS_TOKEN_COOKIE) ??
      this.getStoredValue(AUTH_TOKEN_KEY);
    const refreshToken =
      this.getCookie(REFRESH_TOKEN_COOKIE) ??
      this.getStoredValue(REFRESH_TOKEN_KEY);

    console.info('[Auth] /Auth/RefreshToken çağrılıyor', {
      emailVar: !!email,
      accessTokenVar: !!accessToken,
      refreshTokenVar: !!refreshToken,
      epoch: epochAtStart,
    });

    if (!email || !accessToken || !refreshToken) {
      this.handleExpiredAndLogout(
        `token yenileme: eksik kimlik bilgisi email=${!!email} access=${!!accessToken} refresh=${!!refreshToken}`,
        epochAtStart
      );
      return of(null);
    }

    return this.accountApiService
      .refreshToken(email, accessToken, refreshToken)
      .pipe(
        map((res) => {
          if (epochAtStart !== this.sessionEpoch) {
            console.warn(
              '[Auth] Eski yenileme yanıtı yok sayıldı (oturum handoff/login sonrası)',
              { epochAtStart, epochNow: this.sessionEpoch }
            );
            return this._token();
          }
          if (res.success && res.data?.accessToken) {
            console.info('[Auth] Token yenileme başarılı — yeni erişim tokenı alındı');
            const rememberMe = this.resolveRememberMePreference();
            this.setAuthState(res.data, rememberMe);
            return res.data.accessToken;
          }
          // Refresh başarısızsa her zaman oturumu kapat (önceden message yoksa sessiz kalıyordu)
          console.warn('[Auth] Yenileme yanıtı başarısız → oturum kapatılıyor', res.message);
          this.handleExpiredAndLogout(
            `token yenileme başarısız: basarili=${res.success} mesaj=${res.message ?? '(boş)'}`,
            epochAtStart
          );
          return null;
        }),
        catchError((error: unknown) => {
          if (epochAtStart !== this.sessionEpoch) {
            console.warn(
              '[Auth] Eski yenileme hatası yok sayıldı (oturum handoff/login sonrası)',
              { epochAtStart, epochNow: this.sessionEpoch }
            );
            return of(this._token());
          }
          // Sadece sunucu açıkça refresh token'ı reddederse (400, 401) veya refresh token süresi dolmuşsa oturumu kapat.
          // Geçici ağ hatalarında (0, 5xx) kullanıcıyı anında logout etme!
          const isAuthRejection =
            error instanceof HttpErrorResponse && (error.status === 401 || error.status === 400);
          const status = error instanceof HttpErrorResponse ? error.status : 'bilinmiyor';

          if (this.isRefreshTokenExpired() || isAuthRejection) {
            console.warn(`[Auth] HTTP ${status} kimlik reddi → oturum kapatılıyor`);
            this.handleExpiredAndLogout(`token yenileme HTTP ${status}`, epochAtStart);
          } else {
            console.warn(
              `[Auth] HTTP ${status} geçici hata — oturum kapatılmadı, sonraki istek tekrar dener`
            );
          }
          return of(null);
        })
      );
  }

  /**
   * Guard / SignalR gibi dış çağrılar için public oturum-sonu bildirimi.
   */
  reportSessionEnd(reason: string, detail?: string): void {
    this.logSessionEnd(reason, detail);
  }

  /**
   * Console'da oturum sonlanma nedenini net gösterir.
   * Cross-app redirect (CAD/Lab → Account) console'u sildiği için localStorage'a da yazar;
   * login sayfası replayPersistedSessionEndLog() ile tekrar basar.
   * `reason` makine kodu saklanır (UserLogout / SessionExpired / ForceLogout);
   * konsola okunabilir Türkçe etiket basılır.
   */
  private logSessionEnd(reason: string, detail?: string): void {
    const label = this.sessionEndConsoleLabel(reason);
    const style = 'font-size:13px;font-weight:700;color:#b45309;background:#fff7ed;padding:2px 6px;border-radius:4px';
    if (detail) {
      console.warn(`%c[Auth] ${label}`, style, detail);
    } else {
      console.warn(`%c[Auth] ${label}`, style);
    }

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(
          AUTH_SESSION_END_REASON_KEY,
          JSON.stringify({
            reason,
            detail: detail ?? null,
            at: new Date().toISOString(),
          })
        );
      }
    } catch {
      // ignore quota / private mode
    }
  }

  private sessionEndConsoleLabel(reason: string): string {
    switch (reason) {
      case AUTH_SESSION_END_USER_LOGOUT:
        return 'Kullanıcı çıkış yaptı';
      case AUTH_SESSION_END_EXPIRED:
        return 'Oturum süresi doldu';
      case AUTH_SESSION_END_FORCE_LOGOUT:
        return 'Zorunlu çıkış';
      default:
        return reason;
    }
  }

  /**
   * Redirect sonrası login ekranında son oturum kapanma nedenini tekrar yazar.
   * @returns Persist edilen reason kodu (yoksa null)
   */
  replayPersistedSessionEndLog(): string | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      const raw = localStorage.getItem(AUTH_SESSION_END_REASON_KEY);
      if (!raw) {
        return null;
      }
      localStorage.removeItem(AUTH_SESSION_END_REASON_KEY);
      const parsed = JSON.parse(raw) as { reason?: string; detail?: string | null; at?: string };
      if (!parsed?.reason) {
        return null;
      }
      const label = this.sessionEndConsoleLabel(parsed.reason);
      const style = 'font-size:14px;font-weight:700;color:#b45309;background:#fff7ed;padding:4px 8px;border-radius:4px';
      const when = parsed.at ? ` @ ${parsed.at}` : '';
      if (parsed.detail) {
        console.warn(`%c[Auth] ${label}${when}`, style, parsed.detail);
      } else {
        console.warn(`%c[Auth] ${label}${when}`, style);
      }
      return parsed.reason;
    } catch (err) {
      console.warn('[Auth] Oturum kapanma nedeni yeniden yazılamadı', err);
      return null;
    }
  }

  /**
   * Token / refresh süresi veya auth reddi nedeniyle oturumu kapatır.
   * "Session ended" (ForceLogout) dialog yalnızca SignalR / diğer-sekme ForceLogout
   * broadcast ile açılır — refresh 401'ini "başka cihaz" sanmak account→lab handoff'ta
   * yanlış uyarıya yol açıyordu (yeni kayıt sonrası geçiş dahil).
   * @param expectedEpoch Verilmişse ve oturum epoch değiştiyse (handoff) logout yapılmaz.
   */
  private handleExpiredAndLogout(detail?: string, expectedEpoch?: number): void {
    if (expectedEpoch !== undefined && expectedEpoch !== this.sessionEpoch) {
      console.warn(
        '[Auth] Oturum kapatma atlandı — eski yenileme (epoch değişti)',
        detail,
        { expectedEpoch, epochNow: this.sessionEpoch }
      );
      return;
    }

    if (this.forceLogoutUiPending) {
      console.warn(
        '[Auth] Oturum kapatma atlandı — ForceLogout dialog bekleniyor',
        detail
      );
      if (this._isAuthenticated()) {
        this.forceLogoutRequestSubject.next();
      }
      return;
    }

    this.logSessionEnd(AUTH_SESSION_END_EXPIRED, detail);
    this.endSessionAndRedirect(AUTH_SESSION_END_EXPIRED);
  }

  /**
   * Ortak oturum temizleme + login yönlendirme (log çağıran tarafta yapılır)
   * @param broadcastReason Diğer sekmelere gönderilecek reason kodu
   * @param broadcast Broadcast edilsin mi
   */
  private endSessionAndRedirect(
    broadcastReason: string = AUTH_SESSION_END_EXPIRED,
    broadcast = true
  ): void {
    this.clearSilentRefreshTimer();
    this.stopLogoutBeaconWatch();
    this._token.set(null);
    this._user.set(null);
    this._isAuthenticated.set(false);
    this._error.set(null);
    this.sessionHadSharedCookies = false;
    this.clearStoredAuth();
    if (broadcast) {
      this.broadcastLogout(broadcastReason);
    }
    this.navigateToAccountLogin();
  }

  /**
   * Access token süresi dolmadan önce arka planda sessizce yenileme planlar (proaktif yenileme).
   * Kullanıcı CAD viewer gibi API çağrısı yapmadığı sayfalarda uzun süre kalsa dahi oturumun canlı kalmasını sağlar.
   */
  private scheduleSilentRefresh(expiration?: string | Date | null): void {
    this.clearSilentRefreshTimer();

    if (typeof window === 'undefined') {
      return;
    }

    let expStr: string | null = null;
    if (expiration) {
      expStr = expiration instanceof Date ? expiration.toISOString() : expiration;
    } else {
      expStr = this.getCookie(TOKEN_EXPIRES_COOKIE) ?? this._user()?.expiration ?? null;
    }

    if (!expStr) {
      return;
    }

    try {
      const expDate = new Date(expStr);
      if (isNaN(expDate.getTime())) {
        return;
      }

      const now = Date.now();
      const expTime = expDate.getTime();
      // Token süresinin bitmesine 2 dakika kala yenile
      const leadTimeMs = 2 * 60 * 1000;
      let delayMs = expTime - now - leadTimeMs;

      // Eğer süre zaten dolmak üzereyse veya geçmişse, 5 saniye içinde yenilemeyi dene
      if (delayMs <= 0) {
        delayMs = 5000;
      }

      // Maksimum setTimeout sınırını (24.8 gün) aşmaması için kontrol
      if (delayMs > 2147483647) {
        delayMs = 2147483647;
      }

      console.info('[Auth][Sessiz] Yenileme planlandı', {
        kacSaniyeSonra: Math.round(delayMs / 1000),
        accessBitisZamani: expStr,
      });

      this.silentRefreshTimer = setTimeout(() => {
        if (this._isAuthenticated() && !this.isRefreshTokenExpired()) {
          console.info('[Auth][Sessiz] Zamanlayıcı çalıştı — zorla yenileme başlıyor');
          // forceRefresh: ensureValidToken lead-time'da no-op kalıyordu (access henüz "expired" değil)
          this.forceRefreshToken().subscribe({
            next: (token) => {
              if (token) {
                console.info('[Auth][Sessiz] Zorla yenileme başarılı');
              } else if (this._isAuthenticated() && !this.isRefreshTokenExpired()) {
                // Ağ hatası vb. — bir sonraki API çağrısı yeniden dener; timer'ı kısa süre sonra tekrar kur
                console.warn('[Auth][Sessiz] Geçici hata — kısa süre sonra yeniden planlanıyor');
                this.scheduleSilentRefresh();
              }
            },
            error: (err) => {
              console.warn('[Auth][Sessiz] Yenileme hatası — yeniden planlanıyor', err);
              if (this._isAuthenticated() && !this.isRefreshTokenExpired()) {
                this.scheduleSilentRefresh();
              }
            },
          });
        }
      }, delayMs);
    } catch (err) {
      console.warn('[Auth][Sessiz] Planlama hatası:', err);
    }
  }

  private clearSilentRefreshTimer(): void {
    if (this.silentRefreshTimer) {
      clearTimeout(this.silentRefreshTimer);
      this.silentRefreshTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearSilentRefreshTimer();
    this.stopLogoutBeaconWatch();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.onWindowFocus);
      window.removeEventListener('storage', this.onStorageEvent);
    }
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
  }
}

